import { Link, useSearchParams } from 'react-router-dom';
import { useEffect, useMemo } from 'react';
import {
  ArrowLeft,
  ArrowRight,
  Camera,
  CircleDot,
  Filter,
  HardDrive,
  Moon,
  Move3d,
  PanelsTopLeft,
  RotateCcw,
  ScanEye,
} from 'lucide-react';

import './SecurityCameraLineup.css';

const ImageWithFallback = ({ src, fallback, alt, ...props }) => {
  const handleError = (event) => {
    if (!fallback || event.currentTarget.dataset.fallbackApplied === 'true') return;
    event.currentTarget.dataset.fallbackApplied = 'true';
    event.currentTarget.src = fallback;
  };

  return <img src={src} alt={alt} onError={handleError} {...props} />;
};

/*
 * Add the new white-background HOSHIN images at:
 *
 * /images/security-camera/lineup/hs-dome-standard.png
 * /images/security-camera/lineup/hs-dome-4k.png
 * /images/security-camera/lineup/hs-bullet-standard.png
 * /images/security-camera/lineup/hs-bullet-4k.png
 * /images/security-camera/lineup/hs-night-turret.png
 * /images/security-camera/lineup/hs-ptz.png
 * /images/security-camera/lineup/hs-panoramic-180.png
 *
 * Until those images are added, the page falls back to the current TOP images.
 */

const PRODUCTS = [
  {
    id: 'hs-d4f-001',
    hsModel: 'HS-D4F-001',
    name: '4MP デュアルライト PoEドームカメラ',
    categoryLabel: 'DOME',
    type: 'dome',
    resolution: '4MP',
    lens: '固定焦点',
    image: '/images/security-camera/lineup/hs-dome-standard.png',
    fallbackImage: '/images/product-dome.jpg',
    features: ['night'],
    locations: ['shop', 'office'],
    tags: ['PoE', '4MP', 'デュアルライト'],
    description:
      '店舗・事務所などの標準的な監視用途に適した、固定焦点タイプのPoEドームカメラです。',
  },
  {
    id: 'hs-d5f-001',
    hsModel: 'HS-D5F-001',
    name: '5MP デュアルライト PoEドームカメラ',
    categoryLabel: 'DOME',
    type: 'dome',
    resolution: '5MP',
    lens: '固定焦点 2.8mm',
    image: '/images/security-camera/lineup/hs-dome-standard.png',
    fallbackImage: '/images/product-dome.jpg',
    features: ['night'],
    locations: ['shop', 'office', 'warehouse'],
    tags: ['PoE', '5MP', 'デュアルライト'],
    description:
      '画質と導入しやすさのバランスを重視した、店舗・事務所向けの標準ドームモデルです。',
  },
  {
    id: 'hs-d8f-001',
    hsModel: 'HS-D8F-001',
    name: '8MP（4K）デュアルライト PoEドームカメラ',
    categoryLabel: 'DOME / 4K',
    type: 'dome',
    resolution: '8MP',
    lens: '固定焦点 2.8mm',
    image: '/images/security-camera/lineup/hs-dome-4k.png',
    fallbackImage: '/images/product-4k.jpg',
    features: ['night'],
    locations: ['shop', 'office', 'warehouse'],
    tags: ['PoE', '8MP / 4K', 'デュアルライト'],
    description:
      '細部まで確認しやすい8MP（4K）対応。高画質を重視する場所に適したドームモデルです。',
  },
  {
    id: 'hs-b5f-001',
    hsModel: 'HS-B5F-001',
    name: '5MP デュアルライト PoEバレットカメラ',
    categoryLabel: 'BULLET',
    type: 'bullet',
    resolution: '5MP',
    lens: '固定焦点 2.8mm',
    image: '/images/security-camera/lineup/hs-bullet-standard.png',
    fallbackImage: '/images/product-bullet.jpg',
    features: ['night'],
    locations: ['parking', 'warehouse'],
    tags: ['PoE', '5MP', '屋外監視'],
    description:
      '駐車場・建物外周・搬入口など、監視方向を明確にしたい場所に適したバレットモデルです。',
  },
  {
    id: 'hs-b8f-001',
    hsModel: 'HS-B8F-001',
    name: '8MP（4K）デュアルライト PoEバレットカメラ',
    categoryLabel: 'BULLET / 4K',
    type: 'bullet',
    resolution: '8MP',
    lens: '固定焦点 2.8mm',
    image: '/images/security-camera/lineup/hs-bullet-4k.png',
    fallbackImage: '/images/product-bullet.jpg',
    features: ['night'],
    locations: ['parking', 'warehouse'],
    tags: ['PoE', '8MP / 4K', '屋外監視'],
    description:
      '屋外監視で高画質を重視する場合に適した、8MP（4K）対応のPoEバレットカメラです。',
  },
  {
    id: 'hs-t5f-001',
    hsModel: 'HS-T5F-001',
    name: '5MP IR夜間対応 PoEカメラ',
    categoryLabel: 'IR NIGHT VISION',
    type: 'night',
    resolution: '5MP',
    lens: '固定焦点',
    image: '/images/security-camera/lineup/hs-night-turret.png',
    fallbackImage: '/images/product-night.png',
    features: ['night', 'ir'],
    locations: ['parking', 'warehouse'],
    tags: ['PoE', '5MP', '赤外線'],
    description:
      '照明の少ない場所や夜間の監視を重視する、赤外線撮影対応のPoEカメラです。',
  },
  {
    id: 'hs-t8f-001',
    hsModel: 'HS-T8F-001',
    name: '8MP（4K）IR夜間対応 PoEカメラ',
    categoryLabel: 'IR NIGHT VISION / 4K',
    type: 'night',
    resolution: '8MP',
    lens: '固定焦点',
    image: '/images/security-camera/lineup/hs-night-turret.png',
    fallbackImage: '/images/product-night.png',
    features: ['night', 'ir'],
    locations: ['parking', 'warehouse'],
    tags: ['PoE', '8MP / 4K', '赤外線'],
    description:
      '夜間監視と高画質を両立したい場所に適した、8MP（4K）赤外線対応モデルです。',
  },
  {
    id: 'hs-ptz5-001',
    hsModel: 'HS-PTZ5-001',
    name: '5MP 5倍光学ズーム PoE PTZカメラ',
    categoryLabel: 'PTZ',
    type: 'ptz',
    resolution: '5MP',
    lens: '5倍光学ズーム 2.7–13.5mm',
    image: '/images/security-camera/lineup/hs-ptz.png',
    fallbackImage: '/images/product-ptz.jpg',
    features: ['night', 'ptz'],
    locations: ['parking', 'warehouse'],
    tags: ['PoE', '5MP', '5倍光学ズーム'],
    description:
      'カメラの向きとズームを調整しながら、駐車場・工場・倉庫などの広い範囲を確認できます。',
  },
  {
    id: 'hs-pan8f-001',
    hsModel: 'HS-PAN8F-001',
    name: '8MP（4K）180°パノラマ PoEカメラ',
    categoryLabel: 'PANORAMIC / 180°',
    type: 'panoramic',
    resolution: '8MP',
    lens: 'デュアル 2.8mm',
    image: '/images/security-camera/lineup/hs-panoramic-180.png',
    fallbackImage: '/images/product-180.png',
    features: ['night', 'panoramic'],
    locations: ['shop', 'warehouse'],
    tags: ['PoE', '8MP / 4K', '180°'],
    description:
      '1台で広い範囲を確認したい場所に適した、180°パノラマ撮影対応モデルです。',
  },
];

const CATEGORY_FILTERS = [
  { key: 'all', label: 'すべて', Icon: Filter },
  { key: 'dome', label: 'ドーム', Icon: CircleDot, param: ['type', 'dome'] },
  { key: 'bullet', label: 'バレット', Icon: Camera, param: ['type', 'bullet'] },
  { key: '4k', label: '4K', Icon: ScanEye, param: ['resolution', '8MP'] },
  { key: 'ptz', label: 'PTZ', Icon: Move3d, param: ['type', 'ptz'] },
  { key: 'night', label: '夜間対応', Icon: Moon, param: ['feature', 'night'] },
  {
    key: 'panoramic',
    label: '180°・広角',
    Icon: PanelsTopLeft,
    param: ['type', 'panoramic'],
  },
];

const LOCATION_LABELS = {
  shop: '店舗・飲食店',
  office: 'オフィス・事務所',
  parking: '駐車場・建物外周',
  warehouse: '倉庫・工場',
};

const SecurityCameraLineup = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const previousTitle = document.title;
    const description =
      '株式会社エイチエスの防犯カメララインアップ。ドーム、バレット、4K、PTZ、夜間対応、180°・広角カメラから用途に合わせてお選びいただけます。';

    let metaDescription = document.querySelector('meta[name="description"]');
    const previousDescription = metaDescription?.getAttribute('content') ?? null;
    const createdMetaDescription = !metaDescription;

    document.title = '防犯カメラ ラインアップ｜株式会社エイチエス';

    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }

    metaDescription.setAttribute('content', description);

    return () => {
      document.title = previousTitle;

      if (createdMetaDescription) {
        metaDescription.remove();
      } else if (previousDescription !== null) {
        metaDescription.setAttribute('content', previousDescription);
      }
    };
  }, []);

  const activeFilter = useMemo(() => {
    const type = searchParams.get('type');
    const resolution = searchParams.get('resolution');
    const feature = searchParams.get('feature');

    if (type === 'dome') return 'dome';
    if (type === 'bullet') return 'bullet';
    if (type === 'ptz') return 'ptz';
    if (type === 'panoramic') return 'panoramic';
    if (resolution === '8MP') return '4k';
    if (feature === 'night') return 'night';

    return 'all';
  }, [searchParams]);

  const activeLocation = searchParams.get('location');

  const filteredProducts = useMemo(() => {
    const type = searchParams.get('type');
    const resolution = searchParams.get('resolution');
    const feature = searchParams.get('feature');
    const location = searchParams.get('location');

    return PRODUCTS.filter((product) => {
      if (type && product.type !== type) return false;
      if (resolution && product.resolution !== resolution) return false;
      if (feature && !product.features.includes(feature)) return false;
      if (location && !product.locations.includes(location)) return false;
      return true;
    });
  }, [searchParams]);

  const applyCategoryFilter = (filter) => {
    const next = new URLSearchParams(searchParams);

    next.delete('type');
    next.delete('resolution');
    next.delete('feature');

    if (filter.param) {
      next.set(filter.param[0], filter.param[1]);
    }

    setSearchParams(next);
  };

  const clearAllFilters = () => {
    setSearchParams({});
  };

  return (
    <main className="scl-page" id="page-top">
      <section className="scl-hero">
        <div className="scl-container">
          <Link to="/services/security-camera" className="scl-back-link">
            <ArrowLeft size={14} strokeWidth={1.7} aria-hidden="true" />
            防犯カメラTOPへ戻る
          </Link>

          <div className="scl-hero-copy">
            <p className="scl-eyebrow">SECURITY CAMERA LINEUP</p>
            <h1>防犯カメラ ラインアップ</h1>
            <span className="scl-title-rule" aria-hidden="true" />
            <p>
              設置場所や監視目的に合わせて、カメラタイプ・解像度・夜間性能などからお選びいただけます。
            </p>
          </div>
        </div>
      </section>

      <section className="scl-filter-section" aria-label="カメラ絞り込み">
        <div className="scl-container">
          <div className="scl-filter-head">
            <div>
              <p className="scl-section-kicker">FILTER</p>
              <h2>カメラタイプから絞り込む</h2>
            </div>

            {(activeFilter !== 'all' || activeLocation) && (
              <button
                type="button"
                className="scl-reset"
                onClick={clearAllFilters}
              >
                <RotateCcw size={14} strokeWidth={1.8} aria-hidden="true" />
                絞り込みを解除
              </button>
            )}
          </div>

          <div className="scl-filter-list">
            {CATEGORY_FILTERS.map(({ key, label, Icon, ...filter }) => (
              <button
                key={key}
                type="button"
                className={`scl-filter-button ${
                  activeFilter === key ? 'is-active' : ''
                }`}
                onClick={() => applyCategoryFilter({ key, label, Icon, ...filter })}
                aria-pressed={activeFilter === key}
              >
                <Icon size={17} strokeWidth={1.7} aria-hidden="true" />
                {label}
              </button>
            ))}
          </div>

          {activeLocation && LOCATION_LABELS[activeLocation] && (
            <div className="scl-location-filter">
              <span>設置場所</span>
              <strong>{LOCATION_LABELS[activeLocation]}</strong>
            </div>
          )}
        </div>
      </section>

      <section className="scl-products-section">
        <div className="scl-container">
          <div className="scl-results-head">
            <div>
              <p className="scl-section-kicker">PRODUCTS</p>
              <h2>防犯カメラ</h2>
            </div>
            <p className="scl-result-count">
              <strong>{filteredProducts.length}</strong> 製品
            </p>
          </div>

          {filteredProducts.length > 0 ? (
            <div className="scl-product-grid">
              {filteredProducts.map((product) => (
                <article className="scl-product-card" key={product.id}>
                  <div className="scl-product-image">
                    <ImageWithFallback
                      src={product.image}
                      fallback={product.fallbackImage}
                      alt={product.name}
                      loading="lazy"
                    />
                  </div>

                  <div className="scl-product-body">
                    <div className="scl-product-topline">
                      <span className="scl-product-category">
                        {product.categoryLabel}
                      </span>
                      <span className="scl-product-model">
                        {product.hsModel}
                      </span>
                    </div>

                    <h3>{product.name}</h3>
                    <p className="scl-product-description">
                      {product.description}
                    </p>

                    <dl className="scl-mini-specs">
                      <div>
                        <dt>解像度</dt>
                        <dd>{product.resolution}</dd>
                      </div>
                      <div>
                        <dt>レンズ</dt>
                        <dd>{product.lens}</dd>
                      </div>
                    </dl>

                    <ul className="scl-tag-list" aria-label="主な特長">
                      {product.tags.map((tag) => (
                        <li key={tag}>{tag}</li>
                      ))}
                    </ul>

                    <Link
                      to={`/contact?subject=${encodeURIComponent(
                        `${product.hsModel} ${product.name}`,
                      )}`}
                      className="scl-product-link"
                    >
                      この製品について問い合わせる
                      <ArrowRight
                        size={15}
                        strokeWidth={1.7}
                        aria-hidden="true"
                      />
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="scl-empty">
              <p>現在の条件に一致する製品がありません。</p>
              <button type="button" onClick={clearAllFilters}>
                絞り込みを解除する
              </button>
            </div>
          )}
        </div>
      </section>

      <section className="scl-recorder-section">
        <div className="scl-container">
          <div className="scl-recorder-panel">
            <span className="scl-recorder-icon">
              <HardDrive size={26} strokeWidth={1.55} aria-hidden="true" />
            </span>

            <div className="scl-recorder-copy">
              <p className="scl-section-kicker">RECORDING SYSTEM</p>
              <h2>NVR録画機・録画システム</h2>
              <p>
                カメラ台数や必要な録画日数に合わせて、PoE対応NVRや録画システムを選定します。
              </p>
            </div>

            <Link
              to="/services/security-camera/nvr-recorder"
              className="scl-recorder-link"
            >
              録画機を見る
              <ArrowRight size={16} strokeWidth={1.7} aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>

      <section className="scl-contact">
        <div className="scl-container scl-contact-inner">
          <div>
            <p className="scl-contact-kicker">SELECTION SUPPORT</p>
            <h2>どのカメラを選べばよいか分からない場合</h2>
            <p>
              設置場所、撮影範囲、夜間の明るさ、必要な録画日数などを確認しながら、
              用途に合った機器構成をご案内します。
            </p>
          </div>

          <Link to="/contact" className="scl-contact-link">
            防犯カメラについて相談する
            <ArrowRight size={16} strokeWidth={1.7} aria-hidden="true" />
          </Link>
        </div>
      </section>
    </main>
  );
};

export default SecurityCameraLineup;
